<script setup lang="ts">
// NPM
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

// Components
import {
  BaseAvatar,
  BaseBadge,
  BaseButton,
  BaseIcon,
} from '@magma-app/magma-lapilli';

// Types
import type { PropType } from 'vue';

// Helpers
import AnalyticsHelper from '@/helpers/analytics';

// Stores
import { useChallengesStore } from '@/stores/challenges';
import { useCommonStore } from '@/stores/common';
import { useUserStore } from '@/stores/user';
import type { UserDashboardGetAllChallengesResponseType } from '@magma-app/magma-types/all';
const challengesStore = useChallengesStore();
const commonStore = useCommonStore();
const userStore = useUserStore();

// Plugins
const { t } = useI18n({ useScope: 'global' });

// Props
const props = defineProps({
  challenge: {
    type: Object as PropType<
      UserDashboardGetAllChallengesResponseType['validated'][number]
    >,
    required: false,
  },
  completed: {
    type: Boolean,
    required: false,
  },
});

// Reactive variables
const isSaving = ref(false);

// Computed
const completedChallengeStatus = computed(() => {
  if (props.challenge?.status === 'toValidate') {
    return { color: 'yellow', text: t('generic.validation.pending') };
  } else if (props.challenge?.status === 'rejected') {
    return { color: 'red', text: t('generic.validation.declined') };
  } else {
    return { color: 'green', text: t('generic.validation.completed') };
  }
});

// Event emitters
const emit = defineEmits(['close']);

// Event listeners
const onClose = () => {
  emit('close');
};

const onContactAdmin = async () => {
  window.open(
    `mailto:${userStore.user?.organization?.notificationEmail}`,
    '_blank'
  );

  AnalyticsHelper.trackEvent('ask_support', {
    event_properties: 'challenge',
    user_properties: 'helper',
  });
};

const onStartingChallenge = async (challenge?: {
  status?: string | null;
  id?: number;
}) => {
  if (isSaving.value) return;
  isSaving.value = true;

  if (!challenge?.status && challenge?.id) {
    await challengesStore.startChallenge(challenge.id);

    AnalyticsHelper.trackEvent('challenge_doing', {
      user_properties: 'helper',
    });
  }

  isSaving.value = false;
};

const onValidatingChallenge = async (challenge?: {
  userChallengeId?: number | null;
}) => {
  if (isSaving.value) return;
  isSaving.value = true;

  if (challenge?.userChallengeId) {
    await challengesStore.accomplishChallenge(challenge.userChallengeId);

    AnalyticsHelper.trackEvent('challenge_done', {
      user_properties: 'helper',
    });
  }

  commonStore.setManageChallengesModal({
    open: true,
    item: challenge,
    role: 'challenge-completed',
    title: t('generic.completedChallenge'),
  });

  isSaving.value = false;
};
</script>

<template>
  <div class="flex h-full w-full flex-col justify-between">
    <div>
      <div
        class="flex cursor-pointer content-center"
        :class="'mb-4'"
        @click="onClose"
      >
        <BaseIcon color="#5F6C85" icon="chevron-left" />

        <span
          class="ml-1 text-mgm-txt-sm font-normal text-foreground-default"
          >{{
            completed
              ? $t('generic.completedChallenges')
              : $t('generic.allChallenges')
          }}</span
        >
      </div>

      <div v-if="challenge" class="relative">
        <img
          class="mb-8 h-[25vh] w-full rounded-[5px] border border-transparent object-cover"
          :src="challenge.pictureUrl ? challenge.pictureUrl : '/img/challenge_lg.png'"
        />
        <span
          class="absolute inset-0 flex items-center justify-center text-mgm-txt-sm font-medium text-foreground-brand-default"
          >{{ challenge?.points }}</span
        >
      </div>

      <img
        v-else
        class="mb-8 h-[30vh] w-full rounded-[5px] border border-transparent object-cover"
        :src="
          props.challenge?.pictureUrl
            ? props.challenge?.pictureUrl
            : '/img/reward_lg.png'
        "
      />

      <div class="flex items-start justify-between">
        <h3
          class="mr-4 text-mgm-txt-lg font-medium text-foreground-emphasis"
          :class="{ 'max-w-[80%]': !challenge }"
        >
          {{ challenge?.name }}
        </h3>
      </div>

      <BaseBadge
        v-if="challenge && completed"
        class="mt-2"
        :color="completedChallengeStatus.color"
        :text="completedChallengeStatus.text"
        size="md"
      ></BaseBadge>

      <div
        class="rich-text-content mt-4 text-mgm-txt-sm font-normal text-foreground-default"
        v-html="props.challenge?.description"
      ></div>
    </div>

    <div class="sticky bottom-0 bg-background-default py-6">
      <template v-if="challenge">
        <BaseButton
          v-if="completed && challenge.status === 'toValidate'"
          :text="$t('generic.adminContact')"
          class="mt-2 w-full"
          size="md"
          state="scdr"
          @click="onContactAdmin"
        />
        <BaseButton
          class="mb-2 w-full"
          :disabled="props.challenge?.status === 'interested' || isSaving"
          :text="$t('generic.cta.enroll')"
          @click="onStartingChallenge(props.challenge)"
        />

        <BaseButton
          class="w-full"
          :disabled="!props.challenge?.status || isSaving"
          :text="$t('generic.done')"
          @click="onValidatingChallenge(props.challenge)"
        />
      </template>
    </div>
  </div>
</template>
